/**
 * Public Profile showcase: photo + banner header, social actions, stats, loadout.
 */
import { postDotsTransfer, shopEquipCosmetic } from "../lib/cloud";
import {
  applyDotsFromApi,
  getDotsBalance,
  isCosmeticEquipped,
  setEquippedCosmeticIds,
} from "./dots-currency";
import {
  COSMETIC_ART_SIZE,
  COSMETIC_REF_CURSOR_R,
  drawCosmeticOverlay,
  getCachedCosmeticImage,
  loadCosmeticImage,
} from "./cursor-cosmetics";
import { startCosmeticPreviewLoop, stopCosmeticPreviewLoop } from "./cosmetic-preview-loop";
import { createModalCloseButton } from "./modal-close-button";
import { ANIM_FRAME_MS } from "./shop-creator-fx";
import { paintBannerCardArt } from "./banner-card-preview";
import {
  PROFILE_PHOTO_ZOOM_MAX,
  PROFILE_PHOTO_ZOOM_MIN,
  PROFILE_PHOTO_BG_DEFAULT,
  normalizeProfilePhotoConfig,
  profilePhotoBackgroundColor,
  type ProfilePhotoConfig,
} from "../../lib/profilePhoto";
import { COSMETIC_CATEGORY_LABELS, type CosmeticCategory } from "../../lib/shopTypes";

export type ProfileFriendship = {
  isSelf: boolean;
  isFriend: boolean;
  outgoingPending: boolean;
  incomingPending: boolean;
};

export type ProfileShowcaseCosmetic = {
  id: string;
  name: string;
  imageData: string;
  outlineMaskData?: string | null;
  animationFrames?: string[] | null;
  category?: string;
  allowCustomColor?: boolean;
  matchCursorColor?: boolean;
  cursorColorShade?: number;
  matchCursorMaskData?: string | null;
  matchCursorMaskFrames?: string[] | null;
  activeCustomColor?: string | null;
  ownedCustomColors?: string[];
  equipped?: boolean;
  newCursor?: boolean;
  trailMode?: string | null;
  trailColors?: string[] | null;
};

export type ProfileShowcaseStats = {
  bests?: Record<string, number>;
  totalDeaths?: number;
  totalPlayTimeSec?: number;
  favoriteModeId?: string | null;
  favoriteModeLabel?: string | null;
};

export type ProfileShowcaseBattlePass = {
  seasonNumber: number;
  seasonName: string;
  level: number;
  xp: number;
  xpToNext: number;
};

export type ProfileShowcaseData = {
  user: {
    id: string;
    displayName?: string | null;
    profileBio?: string | null;
  };
  cursorColor?: string;
  dotsBalance?: number;
  battlePass?: ProfileShowcaseBattlePass | null;
  cosmetics?: ProfileShowcaseCosmetic[];
  equippedCosmeticIds?: string[];
  profilePhoto?: ProfilePhotoConfig | null;
  stats?: ProfileShowcaseStats;
  friendship?: ProfileFriendship;
};

export type ProfileShowcaseHandlers = {
  resolveCosmeticDisplay: (
    item: ProfileShowcaseCosmetic,
    cursorColor: string,
  ) => Promise<{ imageData: string; animationFrames: string[] | null }>;
  onBack: () => void;
  onRefresh: () => void;
  onOpenChat: (userId: string) => void;
  onFriendRequest: (userId: string) => Promise<void>;
  onUnfriend: (userId: string, displayName: string) => Promise<void>;
  onUseSandboxPreset?: (preset: { name: string; config: unknown }) => void;
  formatTime: (sec: number) => string;
  /** Own profile: open photo editor */
  onEditPhoto?: () => void;
  /** Own profile: current user id */
  viewerUserId?: string | null;
  isSignedIn: () => boolean;
  /** modeId → whether a compatible replay exists for that user's current best */
  replayAvailableByMode?: Record<string, { available: boolean }>;
  onViewReplay?: (modeId: string) => void;
};

const FRIEND_ADD_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M15 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm-7 2c-3.1 0-7 1.6-7 4.7V21h10.2v-1.5c0-1.5.5-2.8 1.3-3.8C11.4 14.6 9.7 14 8 14zm11.5-1v-2h-2v2h-2v2h2v2h2v-2h2v-2z"/></svg>';
const FRIEND_REMOVE_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M15 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm-7 2c-3.1 0-7 1.6-7 4.7V21h10.2v-1.5c0-1.5.5-2.8 1.3-3.8C11.4 14.6 9.7 14 8 14zm14.5 1h-6v2h6v-2z"/></svg>';
const CHAT_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8A2.5 2.5 0 0 1 17.5 16H10l-4.2 3.4c-.7.55-1.8.06-1.8-.8V16H6.5A2.5 2.5 0 0 1 4 13.5v-8z"/></svg>';
const PENCIL_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>';

const STATS_MODE_CARDS: Array<{ key: string; title: string }> = [
  { key: "progression", title: "Progression" },
  { key: "normal", title: "Normal" },
  { key: "wasd", title: "WASD" },
  { key: "superdot", title: "Superdot" },
  { key: "roulette", title: "Roulette" },
  { key: "sandbox", title: "Sandbox" },
  { key: "highway", title: "Highway" },
  { key: "darkness", title: "Darkness" },
  { key: "mirror", title: "Mirror" },
  { key: "snake", title: "Snake" },
  { key: "combo", title: "Combo" },
  { key: "overload", title: "Overload" },
  { key: "colorrush", title: "Color Rush" },
  { key: "heat", title: "Heat" },
  { key: "hunter", title: "Hunter" },
  { key: "pop", title: "POP" },
  { key: "counter", title: "Counter" },
  { key: "aim", title: "AIM" },
  { key: "hook", title: "Hook" },
  { key: "tilt", title: "Tilt" },
  { key: "orbit", title: "Orbit" },
];

function catLabel(cat: string | undefined): string {
  const c = (cat || "cursor") as CosmeticCategory;
  return COSMETIC_CATEGORY_LABELS[c] || cat || "Cosmetic";
}

function orderedEquipped(
  cosmetics: ProfileShowcaseCosmetic[],
  equippedIds: string[] | undefined,
): ProfileShowcaseCosmetic[] {
  const byId = new Map(cosmetics.map((c) => [c.id, c]));
  const ids = Array.isArray(equippedIds) ? equippedIds : [];
  const ordered: ProfileShowcaseCosmetic[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    const item = byId.get(id);
    if (!item || seen.has(id)) continue;
    seen.add(id);
    ordered.push(item);
  }
  if (!ordered.length) {
    for (const item of cosmetics) {
      if (item.equipped && item.id && !seen.has(item.id)) {
        seen.add(item.id);
        ordered.push(item);
      }
    }
  }
  return ordered;
}

function playLoadout(equipped: ProfileShowcaseCosmetic[]): ProfileShowcaseCosmetic[] {
  return equipped.filter((c) => c.category !== "banner" && c.category !== "trail");
}

function equippedBanner(equipped: ProfileShowcaseCosmetic[]): ProfileShowcaseCosmetic | null {
  return equipped.find((c) => c.category === "banner") || null;
}

/** Most repeated opaque pixel color (quantized), for banner→photo background sync. */
export function dominantOpaqueHexFromImage(
  src: CanvasImageSource | null | undefined,
): string | null {
  if (!src) return null;
  const iw =
    (src as HTMLImageElement).naturalWidth ||
    (src as HTMLCanvasElement).width ||
    0;
  const ih =
    (src as HTMLImageElement).naturalHeight ||
    (src as HTMLCanvasElement).height ||
    0;
  if (iw <= 0 || ih <= 0) return null;
  const tw = 48;
  const th = Math.max(1, Math.round((ih / iw) * tw));
  const c = document.createElement("canvas");
  c.width = tw;
  c.height = th;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, tw, th);
  ctx.drawImage(src, 0, 0, tw, th);
  let data: ImageData;
  try {
    data = ctx.getImageData(0, 0, tw, th);
  } catch {
    return null;
  }
  const counts = new Map<number, number>();
  const { data: px } = data;
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3] < 140) continue;
    // Quantize to 5 bits/channel to cluster near-identical pixels.
    const r = px[i] & 0xf8;
    const g = px[i + 1] & 0xf8;
    const b = px[i + 2] & 0xf8;
    const key = (r << 16) | (g << 8) | b;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  let bestKey = -1;
  let bestCount = 0;
  for (const [key, n] of counts) {
    if (n > bestCount) {
      bestCount = n;
      bestKey = key;
    }
  }
  if (bestKey < 0 || bestCount <= 0) return null;
  const r = (bestKey >> 16) & 0xff;
  const g = (bestKey >> 8) & 0xff;
  const b = bestKey & 0xff;
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

export async function resolveBannerDominantBackground(
  banner: ProfileShowcaseCosmetic | null | undefined,
  cursorColor: string,
  resolve: ProfileShowcaseHandlers["resolveCosmeticDisplay"],
): Promise<string | null> {
  if (!banner) return null;
  const layer = await resolve(banner, cursorColor);
  const img = await loadCosmeticImage(layer.imageData);
  if (!img) return null;
  // Prefer static art; if mostly empty, try first animation frame.
  let hex = dominantOpaqueHexFromImage(img);
  if (!hex && layer.animationFrames?.[0]) {
    const f = await loadCosmeticImage(layer.animationFrames[0]);
    hex = dominantOpaqueHexFromImage(f);
  }
  return hex;
}

async function paintBanner(
  canvas: HTMLCanvasElement,
  banner: ProfileShowcaseCosmetic | null,
  resolve: ProfileShowcaseHandlers["resolveCosmeticDisplay"],
  cursorColor: string,
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  stopCosmeticPreviewLoop(canvas);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "rgba(20, 28, 48, 0.85)";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  if (!banner) {
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = '10px "Press Start 2P", monospace';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("No banner", canvas.width / 2, canvas.height / 2);
    return;
  }
  const layer = await resolve(banner, cursorColor);
  if (!canvas.isConnected) return;
  await loadCosmeticImage(layer.imageData);
  if (layer.animationFrames?.length) {
    await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
  }
  if (!canvas.isConnected) return;

  const drawFrame = () => {
    if (!canvas.isConnected) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(20, 28, 48, 0.85)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    let src = getCachedCosmeticImage(layer.imageData);
    if (layer.animationFrames?.length) {
      const fi = Math.floor(performance.now() / ANIM_FRAME_MS) % layer.animationFrames.length;
      src = getCachedCosmeticImage(layer.animationFrames[fi]) || src;
    }
    if (!src) return;
    const iw = src.naturalWidth || src.width || 256;
    const ih = src.naturalHeight || src.height || 64;
    const scale = Math.min(canvas.width / iw, canvas.height / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(src, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh);
  };
  drawFrame();
  if (layer.animationFrames?.length) {
    startCosmeticPreviewLoop(canvas, drawFrame, () => canvas.isConnected);
  }
}

export async function paintProfilePhoto(
  canvas: HTMLCanvasElement,
  photo: ProfilePhotoConfig | null | undefined,
  cosmetics: ProfileShowcaseCosmetic[],
  resolve: ProfileShowcaseHandlers["resolveCosmeticDisplay"],
  cursorColor: string,
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  stopCosmeticPreviewLoop(canvas);
  const cfg = normalizeProfilePhotoConfig(photo);
  let bg = cfg ? profilePhotoBackgroundColor(cfg) : PROFILE_PHOTO_BG_DEFAULT;
  if (cfg?.backgroundFromBanner) {
    const banner = cosmetics.find((c) => c.category === "banner" && c.equipped) || null;
    const synced = await resolveBannerDominantBackground(banner, cursorColor, resolve);
    if (!canvas.isConnected) return;
    if (synced) bg = synced;
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (!cfg) {
    ctx.fillStyle = "rgba(255,255,255,0.4)";
    ctx.font = '9px "Press Start 2P", monospace';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("No photo", canvas.width / 2, canvas.height / 2);
    return;
  }

  const ids =
    Array.isArray(cfg.cosmeticIds) && cfg.cosmeticIds.length
      ? cfg.cosmeticIds
      : cfg.cosmeticId
        ? [cfg.cosmeticId]
        : [];
  const byId = new Map(cosmetics.map((c) => [c.id, c]));
  // Cursor category is exclusive and always the bottom (first) layer.
  const rawItems = ids.map((id) => byId.get(id)).filter((c): c is ProfileShowcaseCosmetic => !!c);
  const cursorItems = rawItems.filter((c) => c.category === "cursor");
  const otherItems = rawItems.filter((c) => c.category !== "cursor");
  const items = cursorItems.length ? [cursorItems[0], ...otherItems] : otherItems;
  if (!items.length) {
    ctx.fillStyle = "rgba(255,255,255,0.4)";
    ctx.font = '9px "Press Start 2P", monospace';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Missing", canvas.width / 2, canvas.height / 2);
    return;
  }

  const layers: Array<{
    imageData: string;
    animationFrames: string[] | null;
    newCursor?: boolean;
  }> = [];
  for (const item of items) {
    const layer = await resolve(item, cursorColor);
    if (!canvas.isConnected) return;
    await loadCosmeticImage(layer.imageData);
    if (layer.animationFrames?.length) {
      await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
    }
    if (!canvas.isConnected) return;
    layers.push({ ...layer, newCursor: item.newCursor });
  }

  const hideBase = layers.some((l) => l.newCursor);
  const drawFrame = () => {
    if (!canvas.isConnected) return;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, canvas.width, canvas.height);
    ctx.clip();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const cx = canvas.width * 0.5 + cfg.offsetX * canvas.width * 0.5;
    const cy = canvas.height * 0.5 + cfg.offsetY * canvas.height * 0.5;
    const base = Math.min(canvas.width, canvas.height) * 0.72 * cfg.zoom;
    const r = base / (COSMETIC_ART_SIZE / COSMETIC_REF_CURSOR_R);

    ctx.translate(cx, cy);
    ctx.rotate((cfg.rotation * Math.PI) / 180);
    ctx.translate(-cx, -cy);

    if (!hideBase) {
      ctx.fillStyle = cursorColor || "#ffffff";
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    for (const layer of layers) {
      drawCosmeticOverlay(ctx, cx, cy, r, getCachedCosmeticImage(layer.imageData));
      if (layer.animationFrames?.length) {
        const fi = Math.floor(performance.now() / ANIM_FRAME_MS) % layer.animationFrames.length;
        drawCosmeticOverlay(ctx, cx, cy, r, getCachedCosmeticImage(layer.animationFrames[fi]));
      }
    }
    ctx.restore();
  };

  drawFrame();
  if (layers.some((l) => l.animationFrames?.length)) {
    startCosmeticPreviewLoop(canvas, drawFrame, () => canvas.isConnected);
  }
}

async function paintPlayCursor(
  canvas: HTMLCanvasElement,
  cursorColor: string,
  cosmetics: ProfileShowcaseCosmetic[],
  equippedIds: string[] | undefined,
  resolve: ProfileShowcaseHandlers["resolveCosmeticDisplay"],
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  stopCosmeticPreviewLoop(canvas);
  const equipped = playLoadout(orderedEquipped(cosmetics, equippedIds));
  const layers: Array<{ imageData: string; animationFrames: string[] | null; newCursor?: boolean }> =
    [];
  for (const item of equipped) {
    const layer = await resolve(item, cursorColor);
    if (!canvas.isConnected) return;
    await loadCosmeticImage(layer.imageData);
    if (layer.animationFrames?.length) {
      await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
    }
    if (!canvas.isConnected) return;
    layers.push({ ...layer, newCursor: item.newCursor });
  }

  const hideBase = layers.some((l) => l.newCursor);
  const drawFrame = () => {
    if (!canvas.isConnected) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    const cx = w * 0.5;
    const cy = h * 0.5;
    const fill = Math.min(w, h) * 0.78;
    const r = fill / (COSMETIC_ART_SIZE / COSMETIC_REF_CURSOR_R);
    if (!hideBase) {
      ctx.save();
      ctx.fillStyle = cursorColor || "#ffffff";
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    for (const layer of layers) {
      drawCosmeticOverlay(ctx, cx, cy, r, getCachedCosmeticImage(layer.imageData));
      if (layer.animationFrames?.length) {
        const fi = Math.floor(performance.now() / ANIM_FRAME_MS) % layer.animationFrames.length;
        drawCosmeticOverlay(ctx, cx, cy, r, getCachedCosmeticImage(layer.animationFrames[fi]));
      }
    }
  };
  drawFrame();
  if (layers.some((l) => l.animationFrames?.length)) {
    startCosmeticPreviewLoop(canvas, drawFrame, () => canvas.isConnected);
  }
}

function appendStatsGrid(
  parent: HTMLElement,
  stats: ProfileShowcaseStats | undefined,
  formatTime: (n: number) => string,
  opts?: {
    replayAvailableByMode?: Record<string, { available: boolean }>;
    onViewReplay?: (modeId: string) => void;
  },
) {
  const wrap = document.createElement("div");
  wrap.className = "profileShowcaseStats";
  const head = document.createElement("h3");
  head.className = "profileShowcaseSectionTitle";
  head.textContent = "Stats";
  wrap.appendChild(head);

  const grid = document.createElement("div");
  grid.className = "statsGrid profileShowcaseStatsGrid";
  const bests = stats?.bests || {};

  for (const def of STATS_MODE_CARDS) {
    const val = Math.floor(Number(bests[def.key]) || 0);
    const card = document.createElement("div");
    card.className = "statsCard";
    const modeEl = document.createElement("div");
    modeEl.className = "statsCardMode";
    modeEl.textContent = def.title;
    card.appendChild(modeEl);
    const recEl = document.createElement("div");
    recEl.className = "statsCardRecord";
    recEl.textContent = String(val);
    card.appendChild(recEl);
    const topEl = document.createElement("div");
    topEl.className = "statsCardTop";
    topEl.textContent = "TOP: —";
    card.appendChild(topEl);
    const replayMeta = opts?.replayAvailableByMode?.[def.key];
    if (
      opts?.onViewReplay &&
      replayMeta &&
      (replayMeta.available === true ||
        (replayMeta.available !== false && (replayMeta as { gameVersion?: string }).gameVersion))
    ) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "viewReplayLink";
      btn.textContent = "View Replay";
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        opts.onViewReplay?.(def.key);
      });
      card.appendChild(btn);
    }
    grid.appendChild(card);
  }

  const playCard = document.createElement("div");
  playCard.className = "statsCard statsCard--wide";
  playCard.innerHTML = `<div class="statsCardMode">Play time</div><div class="statsCardRecord">${formatTime(Math.floor(Number(stats?.totalPlayTimeSec) || 0))}</div>`;
  grid.appendChild(playCard);

  const deathsCard = document.createElement("div");
  deathsCard.className = "statsCard statsCard--wide";
  deathsCard.innerHTML = `<div class="statsCardMode">Deaths</div><div class="statsCardRecord">${Math.floor(Number(stats?.totalDeaths) || 0).toLocaleString()}</div>`;
  grid.appendChild(deathsCard);

  const favCard = document.createElement("div");
  favCard.className = "statsCard statsCard--wide";
  favCard.innerHTML = `<div class="statsCardMode">Favorite Mode</div><div class="statsCardRecord">${stats?.favoriteModeLabel || "—"}</div>`;
  grid.appendChild(favCard);

  wrap.appendChild(grid);
  parent.appendChild(wrap);
}

function appendTransferBlock(
  parent: HTMLElement,
  toUserId: string,
  displayName: string,
  theirDots: number,
  onDone: () => void,
) {
  const wrap = document.createElement("div");
  wrap.className = "profileActionBlock profileDotsTransfer";
  const title = document.createElement("p");
  title.className = "profileActionBlockTitle";
  title.textContent = "Transfer Dots";
  wrap.appendChild(title);

  const bal = document.createElement("p");
  bal.className = "accessHint profileDotsTransferBalances";
  const renderBal = () => {
    bal.textContent = `You: ${getDotsBalance().toLocaleString()}  ·  Them: ${theirDots.toLocaleString()}`;
  };
  renderBal();
  wrap.appendChild(bal);

  const row = document.createElement("div");
  row.className = "formRow profileDotsTransferRow";
  const amountInput = document.createElement("input");
  amountInput.type = "number";
  amountInput.min = "1";
  amountInput.step = "1";
  amountInput.className = "formInput";
  amountInput.placeholder = "Amount";
  amountInput.setAttribute("aria-label", "Dots amount to send");
  const sendBtn = document.createElement("button");
  sendBtn.type = "button";
  sendBtn.className = "btn green";
  sendBtn.textContent = "Send";
  row.appendChild(amountInput);
  row.appendChild(sendBtn);
  wrap.appendChild(row);
  const status = document.createElement("p");
  status.className = "accessHint";
  wrap.appendChild(status);

  let liveTheir = theirDots;
  sendBtn.onclick = async () => {
    const amount = Math.floor(Number(amountInput.value) || 0);
    if (amount < 1) {
      status.textContent = "Enter a positive amount.";
      return;
    }
    if (amount > getDotsBalance()) {
      status.textContent = "Not enough Dots.";
      return;
    }
    sendBtn.disabled = true;
    status.textContent = "Sending…";
    try {
      const res = await postDotsTransfer({ toUserId, amount });
      if (res.dots) applyDotsFromApi(res.dots);
      liveTheir += amount;
      renderBal();
      amountInput.value = "";
      status.textContent = `Sent ${amount.toLocaleString()} Dots to ${res.toDisplayName || displayName}.`;
      onDone();
    } catch (e) {
      const code = e instanceof Error ? e.message : "transfer_failed";
      if (e && typeof e === "object" && "dots" in e) applyDotsFromApi((e as { dots: unknown }).dots as never);
      renderBal();
      if (code === "insufficient_dots") status.textContent = "Not enough Dots.";
      else if (code === "user_not_found") status.textContent = "User not found.";
      else if (code === "cannot_transfer_to_self") status.textContent = "You cannot send Dots to yourself.";
      else if (code === "invalid_amount") status.textContent = "Invalid amount.";
      else status.textContent = "Transfer failed.";
    } finally {
      sendBtn.disabled = false;
    }
  };

  parent.appendChild(wrap);
}

const BANNER_PICKER_CARD_W = 280;
const BANNER_PICKER_CARD_H = 70;

function openBannerEquipPicker(
  banners: ProfileShowcaseCosmetic[],
  cursorColor: string,
  resolve: ProfileShowcaseHandlers["resolveCosmeticDisplay"],
  onDone: () => void,
) {
  const modal = document.createElement("div");
  modal.className = "profileBannerPickerModal screen";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "Equip banner");

  const panel = document.createElement("div");
  panel.className = "modeDetailModalPanel modeBox profileBannerPickerPanel";
  panel.appendChild(createModalCloseButton(() => modal.remove(), "Close banner picker"));

  const title = document.createElement("h2");
  title.textContent = "Banners";
  panel.appendChild(title);

  const hint = document.createElement("p");
  hint.className = "accessHint";
  hint.textContent = "Choose a banner to equip on your profile.";
  panel.appendChild(hint);

  const status = document.createElement("p");
  status.className = "accessHint";
  panel.appendChild(status);

  const grid = document.createElement("div");
  grid.className = "profileBannerPickerGrid";

  if (!banners.length) {
    const empty = document.createElement("p");
    empty.className = "accessHint";
    empty.textContent = "No banners owned. Get one from the Shop.";
    panel.appendChild(empty);
  } else {
    for (const item of banners) {
      const card = document.createElement("article");
      card.className = "inventoryCosmeticCard profileBannerPickerCard";
      if (item.equipped || isCosmeticEquipped(item.id)) {
        card.classList.add("inventoryCosmeticCard--equipped");
      }

      const previewWrap = document.createElement("div");
      previewWrap.className =
        "shopCosmeticCardPreviewWrap shopCosmeticCardPreviewWrap--banner";
      const idleCanvas = document.createElement("canvas");
      idleCanvas.className =
        "shopCosmeticCardPreview shopCosmeticCardPreview--idle shopCosmeticCardPreview--banner";
      idleCanvas.width = BANNER_PICKER_CARD_W;
      idleCanvas.height = BANNER_PICKER_CARD_H;
      const hoverCanvas = document.createElement("canvas");
      hoverCanvas.className =
        "shopCosmeticCardPreview shopCosmeticCardPreview--hover shopCosmeticCardPreview--banner";
      hoverCanvas.width = BANNER_PICKER_CARD_W;
      hoverCanvas.height = BANNER_PICKER_CARD_H;
      hoverCanvas.setAttribute("aria-hidden", "true");
      previewWrap.appendChild(idleCanvas);
      previewWrap.appendChild(hoverCanvas);
      card.appendChild(previewWrap);

      const name = document.createElement("h3");
      name.className = "inventoryCosmeticCardName";
      name.textContent = item.name;
      card.appendChild(name);

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn green inventoryCosmeticCardBtn";
      const equippedNow = item.equipped || isCosmeticEquipped(item.id);
      btn.textContent = equippedNow ? "Unequip" : "Equip";
      btn.onclick = async () => {
        btn.disabled = true;
        status.textContent = "Updating…";
        try {
          const action = isCosmeticEquipped(item.id) ? "unequip" : "equip";
          const res = await shopEquipCosmetic(item.id, action);
          if (res.dots) applyDotsFromApi(res.dots);
          else if (res.equippedCosmeticIds) setEquippedCosmeticIds(res.equippedCosmeticIds);
          modal.remove();
          onDone();
        } catch {
          status.textContent = "Could not update banner.";
          btn.disabled = false;
        }
      };
      card.appendChild(btn);
      grid.appendChild(card);

      void resolve(item, cursorColor).then(async (layer) => {
        await loadCosmeticImage(layer.imageData);
        if (layer.animationFrames?.length) {
          await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
        }
        if (!card.isConnected) return;
        paintBannerCardArt(idleCanvas, layer.imageData, layer.animationFrames, "idle");
        paintBannerCardArt(hoverCanvas, layer.imageData, layer.animationFrames, "preview");
        if (layer.animationFrames?.length) {
          const startHover = () => {
            startCosmeticPreviewLoop(
              hoverCanvas,
              () => paintBannerCardArt(hoverCanvas, layer.imageData, layer.animationFrames, "preview"),
              () =>
                card.isConnected &&
                (card.matches(":hover") || card.classList.contains("inventoryCosmeticCard--preview")),
            );
          };
          const stopHover = () => stopCosmeticPreviewLoop(hoverCanvas);
          card.addEventListener("mouseenter", startHover);
          card.addEventListener("mouseleave", stopHover);
        }
      });
    }
    panel.appendChild(grid);
  }

  modal.appendChild(panel);
  modal.addEventListener("click", (e) => {
    if (e.target === modal) modal.remove();
  });
  document.body.appendChild(modal);
}

export async function renderProfileShowcase(
  root: HTMLElement,
  data: ProfileShowcaseData,
  presets: Array<{ name: string; config: unknown }>,
  resolvedUserId: string,
  handlers: ProfileShowcaseHandlers,
) {
  root.replaceChildren();
  root.classList.add("profilePublicCard", "profilePublicCard--wide");
  root.classList.remove("hidden");

  const u = data.user || { id: resolvedUserId };
  const cosmetics = Array.isArray(data.cosmetics) ? data.cosmetics : [];
  const cursorColor = data.cursorColor || "#ffffff";
  const dotsBalance = Math.max(0, Math.floor(Number(data.dotsBalance) || 0));
  const friendship = data.friendship || {
    isSelf: handlers.viewerUserId === resolvedUserId,
    isFriend: false,
    outgoingPending: false,
    incomingPending: false,
  };
  const isSelf = friendship.isSelf || handlers.viewerUserId === resolvedUserId;
  const equipped = orderedEquipped(cosmetics, data.equippedCosmeticIds);
  const banner = equippedBanner(equipped);

  const actions = document.createElement("div");
  actions.className = "formRow profilePublicProfileActions";
  const backBtn = document.createElement("button");
  backBtn.type = "button";
  backBtn.className = "btn";
  backBtn.textContent = "← Back";
  backBtn.onclick = () => handlers.onBack();
  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "btn green";
  refreshBtn.textContent = "Refresh";
  refreshBtn.onclick = () => handlers.onRefresh();
  actions.appendChild(backBtn);
  actions.appendChild(refreshBtn);
  root.appendChild(actions);

  const name = document.createElement("h3");
  name.className = "profilePublicPlayerName";
  name.textContent = u.displayName || u.id;
  root.appendChild(name);
  if (data.battlePass) {
    const bp = data.battlePass;
    const bpCard = document.createElement("div");
    bpCard.className = "profileShowcaseBattlePass";
    const pct = Math.max(0, Math.min(100, Math.round((bp.xp / Math.max(1, bp.xpToNext)) * 100)));
    bpCard.innerHTML = `<span class="profileShowcaseBpLevel">Lv ${bp.level}</span><div class="profileShowcaseBpMeta"><strong>Season ${bp.seasonNumber}</strong><span>${bp.xp.toLocaleString()} / ${bp.xpToNext.toLocaleString()} XP</span><div class="bpXpBar"><div class="bpXpFill" style="width:${pct}%"></div></div></div>`;
    root.appendChild(bpCard);
  }

  const idLine = document.createElement("p");
  idLine.className = "accessHint";
  idLine.textContent = `User id: ${resolvedUserId}`;
  root.appendChild(idLine);

  const bio = document.createElement("p");
  bio.className = "accessHint";
  bio.textContent = u.profileBio || "No bio.";
  root.appendChild(bio);

  // Photo + Banner header row (flush)
  const hero = document.createElement("div");
  hero.className = "profileHeroRow";
  const photoWrap = document.createElement("div");
  photoWrap.className = "profileHeroPhoto";
  const photoCanvas = document.createElement("canvas");
  photoCanvas.className = "profileHeroPhotoCanvas";
  photoCanvas.width = 160;
  photoCanvas.height = 160;
  photoWrap.appendChild(photoCanvas);
  if (isSelf && handlers.onEditPhoto) {
    const editBtn = document.createElement("button");
    editBtn.type = "button";
    editBtn.className = "btn profileHeroPhotoEditBtn";
    editBtn.textContent = "Edit photo";
    editBtn.onclick = () => handlers.onEditPhoto?.();
    photoWrap.appendChild(editBtn);
  }
  const bannerWrap = document.createElement("div");
  bannerWrap.className = "profileHeroBanner";
  const bannerCanvas = document.createElement("canvas");
  bannerCanvas.className = "profileHeroBannerCanvas";
  bannerCanvas.width = 512;
  bannerCanvas.height = 128;
  bannerWrap.appendChild(bannerCanvas);
  if (isSelf && handlers.isSignedIn()) {
    const editBannerBtn = document.createElement("button");
    editBannerBtn.type = "button";
    editBannerBtn.className = "profileHeroBannerEditBtn";
    editBannerBtn.title = "Change banner";
    editBannerBtn.setAttribute("aria-label", "Change banner");
    editBannerBtn.innerHTML = PENCIL_SVG;
    editBannerBtn.onclick = () => {
      const banners = cosmetics.filter((c) => c.category === "banner");
      openBannerEquipPicker(banners, cursorColor, handlers.resolveCosmeticDisplay, () =>
        handlers.onRefresh(),
      );
    };
    bannerWrap.appendChild(editBannerBtn);
  }
  hero.appendChild(photoWrap);
  hero.appendChild(bannerWrap);
  root.appendChild(hero);

  void paintProfilePhoto(
    photoCanvas,
    data.profilePhoto,
    cosmetics,
    handlers.resolveCosmeticDisplay,
    cursorColor,
  );
  void paintBanner(bannerCanvas, banner, handlers.resolveCosmeticDisplay, cursorColor);

  // Social / transfer actions row
  const socialRow = document.createElement("div");
  socialRow.className = "profileSocialActionsRow";

  if (!isSelf && handlers.isSignedIn()) {
    appendTransferBlock(socialRow, resolvedUserId, u.displayName || u.id, dotsBalance, () => {});

    const friendBlock = document.createElement("div");
    friendBlock.className = "profileActionBlock profileFriendActionBlock";
    const friendTitle = document.createElement("p");
    friendTitle.className = "profileActionBlockTitle";
    friendTitle.textContent = friendship.isFriend ? "Friends" : "Friend request";
    friendBlock.appendChild(friendTitle);
    const friendBtn = document.createElement("button");
    friendBtn.type = "button";
    friendBtn.className = friendship.isFriend
      ? "profileFriendIconBtn profileFriendIconBtn--danger"
      : "profileFriendIconBtn";
    if (friendship.isFriend) {
      friendBtn.title = "Remove friend";
      friendBtn.innerHTML = FRIEND_REMOVE_SVG;
      friendBtn.onclick = () => void handlers.onUnfriend(resolvedUserId, u.displayName || u.id);
    } else if (friendship.outgoingPending) {
      friendBtn.disabled = true;
      friendBtn.title = "Request pending";
      friendBtn.innerHTML = FRIEND_ADD_SVG;
      friendBtn.classList.add("profileFriendIconBtn--pending");
    } else {
      friendBtn.title = "Send friend request";
      friendBtn.innerHTML = FRIEND_ADD_SVG;
      friendBtn.onclick = async () => {
        friendBtn.disabled = true;
        try {
          await handlers.onFriendRequest(resolvedUserId);
          friendBtn.title = "Request pending";
          friendBtn.classList.add("profileFriendIconBtn--pending");
        } catch {
          friendBtn.disabled = false;
        }
      };
    }
    friendBlock.appendChild(friendBtn);
    socialRow.appendChild(friendBlock);

    if (friendship.isFriend) {
      const chatBlock = document.createElement("div");
      chatBlock.className = "profileActionBlock profileChatActionBlock";
      const chatTitle = document.createElement("p");
      chatTitle.className = "profileActionBlockTitle";
      chatTitle.textContent = "Chat";
      chatBlock.appendChild(chatTitle);
      const chatBtn = document.createElement("button");
      chatBtn.type = "button";
      chatBtn.className = "profileFriendIconBtn profileFriendChatBtn";
      chatBtn.title = "Open chat";
      chatBtn.innerHTML = CHAT_SVG;
      chatBtn.onclick = () => handlers.onOpenChat(resolvedUserId);
      chatBlock.appendChild(chatBtn);
      socialRow.appendChild(chatBlock);
    }
  }

  if (socialRow.childNodes.length) root.appendChild(socialRow);

  appendStatsGrid(root, data.stats, handlers.formatTime, {
    replayAvailableByMode: handlers.replayAvailableByMode,
    onViewReplay: handlers.onViewReplay,
  });

  // Equipped play cursor + inventory + dots
  const loadout = document.createElement("div");
  loadout.className = "profileShowcaseLoadout";
  const loadoutTitle = document.createElement("h3");
  loadoutTitle.className = "profileShowcaseSectionTitle";
  loadoutTitle.textContent = "Equipped cursor";
  loadout.appendChild(loadoutTitle);

  const cursorBox = document.createElement("div");
  cursorBox.className = "profilePublicCursorBox modeBox";
  const colorLine = document.createElement("p");
  colorLine.className = "accessHint profilePublicCursorColorLine";
  const swatch = document.createElement("span");
  swatch.className = "profilePublicCursorSwatch";
  swatch.style.background = cursorColor;
  colorLine.appendChild(document.createTextNode("Color "));
  colorLine.appendChild(swatch);
  colorLine.appendChild(document.createTextNode(` ${cursorColor}`));
  cursorBox.appendChild(colorLine);
  const cursorCanvas = document.createElement("canvas");
  cursorCanvas.className = "profilePublicCursorCanvas";
  cursorCanvas.width = 220;
  cursorCanvas.height = 220;
  cursorBox.appendChild(cursorCanvas);
  loadout.appendChild(cursorBox);
  root.appendChild(loadout);
  void paintPlayCursor(
    cursorCanvas,
    cursorColor,
    cosmetics,
    data.equippedCosmeticIds,
    handlers.resolveCosmeticDisplay,
  );

  const dotsLine = document.createElement("p");
  dotsLine.className = "profilePublicDotsLine";
  dotsLine.textContent = `${dotsBalance.toLocaleString()} Dots`;
  root.appendChild(dotsLine);

  const invHead = document.createElement("h3");
  invHead.className = "profileShowcaseSectionTitle";
  invHead.textContent = "Cosmetics";
  root.appendChild(invHead);

  if (!cosmetics.length) {
    const empty = document.createElement("p");
    empty.className = "accessHint";
    empty.textContent = "No cosmetics owned.";
    root.appendChild(empty);
  } else {
    const grid = document.createElement("div");
    grid.className = "profilePublicCosmeticsGrid";
    for (const item of cosmetics) {
      const card = document.createElement("article");
      card.className = "profilePublicCosmeticCard";
      if (item.equipped) card.classList.add("profilePublicCosmeticCard--equipped");
      const thumb = document.createElement("canvas");
      thumb.className =
        item.category === "banner"
          ? "profilePublicCosmeticThumb profilePublicCosmeticThumb--banner"
          : "profilePublicCosmeticThumb";
      thumb.width = item.category === "banner" ? 160 : 96;
      thumb.height = item.category === "banner" ? 40 : 96;
      card.appendChild(thumb);
      const itemName = document.createElement("p");
      itemName.className = "profilePublicCosmeticName";
      itemName.textContent = item.name;
      card.appendChild(itemName);
      const state = document.createElement("p");
      state.className = "accessHint";
      state.textContent = `${catLabel(item.category)} · ${item.equipped ? "Equipped" : "Owned"}`;
      card.appendChild(state);

      grid.appendChild(card);
      void handlers.resolveCosmeticDisplay(item, cursorColor).then(async (layer) => {
        const img = await loadCosmeticImage(layer.imageData);
        const ctx = thumb.getContext("2d");
        if (!ctx || !img) return;
        ctx.clearRect(0, 0, thumb.width, thumb.height);
        const sizeW = thumb.width * 0.95;
        const sizeH = thumb.height * 0.95;
        const iw = img.naturalWidth || img.width || sizeW;
        const ih = img.naturalHeight || img.height || sizeH;
        const scale = Math.min(sizeW / iw, sizeH / ih);
        const dw = iw * scale;
        const dh = ih * scale;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, (thumb.width - dw) / 2, (thumb.height - dh) / 2, dw, dh);
      });
    }
    root.appendChild(grid);
  }

  const sub = document.createElement("h3");
  sub.className = "profileShowcaseSectionTitle";
  sub.textContent = "Sandbox presets (public)";
  root.appendChild(sub);
  if (!presets.length) {
    const p = document.createElement("p");
    p.className = "accessHint";
    p.textContent = "No public sandbox presets for this user.";
    root.appendChild(p);
  } else {
    const ul = document.createElement("ul");
    ul.className = "profilePublicPresetList";
    presets.forEach((p) => {
      const li = document.createElement("li");
      li.appendChild(document.createTextNode(`${p.name} `));
      if (handlers.onUseSandboxPreset) {
        const useBtn = document.createElement("button");
        useBtn.type = "button";
        useBtn.className = "btn green";
        useBtn.textContent = "Use in editor";
        useBtn.onclick = () => handlers.onUseSandboxPreset?.(p);
        li.appendChild(useBtn);
      }
      ul.appendChild(li);
    });
    root.appendChild(ul);
  }
}

export { PROFILE_PHOTO_ZOOM_MIN, PROFILE_PHOTO_ZOOM_MAX, normalizeProfilePhotoConfig };
export type { ProfilePhotoConfig };
